/**
 * src/components/dashboard/ColumnaCifras.jsx
 * La columna derecha de Estado: la lectura del día en tarjetas.
 *
 * Decidido por Manuel (27-08, hilo de distribución): las cifras dejan el pie
 * de la página y pasan a una columna de cuadros a la derecha del parte. Seis
 * tarjetas, en el orden que manda el backend (negocio primero, máquina
 * después). Cada una es clicable si trae `ruta`.
 *
 * El contrato de datos es el de siempre: una cifra sin lectura se pinta «—»,
 * nunca 0 (§3 de docs/ERRORES_RECURRENTES.md). El backend ya distingue los
 * casos; aquí solo se respetan.
 */
import { useNavigate } from 'react-router-dom'
import { Cifra } from '../ui/Cifra'
import { diaISOChile, diaISOChileDe, fechaCL, horaCL } from '../../lib/horaChile'

// Mismos colores que los badges de tier (index.css): el color sigue a la
// entidad en todo el panel, no al gráfico que la pinta.
const TIER_BAR = {
  basic:      'var(--color-neutral)',
  pro:        'var(--color-running)',
  premium:    'var(--color-highlight)',
  enterprise: 'var(--color-accent)',
}

const ROTULO = {
  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
  letterSpacing: '0.11em', textTransform: 'uppercase',
  color: 'var(--color-text-3)', marginBottom: 'var(--space-2)',
}

const NOTA = {
  fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
  color: 'var(--color-text-3)', marginTop: 'var(--space-1)',
}

function Tarjeta({ etiqueta, ruta, children }) {
  const navigate = useNavigate()
  return (
    <div
      onClick={() => ruta && navigate(ruta)}
      title={ruta ? 'Ver detalle' : undefined}
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-4) var(--space-5)',
        cursor: ruta ? 'pointer' : 'default',
        transition: 'transform var(--t-base) var(--ease)',
      }}
      onMouseEnter={e => { if (ruta) e.currentTarget.style.transform = 'translateY(-2px)' }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none' }}
    >
      <div style={ROTULO}>{etiqueta}</div>
      {children}
    </div>
  )
}

/** El valor grande con el trato de siempre: `lectura: 'ahora'` es un recuento
    vivo y se dice «ahora mismo»; una fecha es una lectura; null es «—». */
function ValorVivo({ valor, lectura, sufijo, contexto }) {
  return (
    <div>
      <Cifra
        valor={valor}
        lectura={lectura === 'ahora' ? new Date() : lectura}
        sufijo={sufijo}
        nada="ninguno"
        tamano="var(--text-xl)"
        pie={lectura !== 'ahora'}
      />
      {lectura === 'ahora' && <div style={NOTA}>ahora mismo</div>}
      {contexto && <div style={NOTA}>{contexto}</div>}
    </div>
  )
}

// ── tenants: total + desglose pago / trial / otros ──────────────────────────
function TarjetaDesglose({ c }) {
  return (
    <Tarjeta etiqueta={c.etiqueta} ruta={c.ruta}>
      <ValorVivo valor={c.valor} lectura={c.lectura} />
      <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-1)' }}>
        {(c.partes || []).map(p => (
          <div key={p.clave} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            fontSize: 'var(--text-sm)', color: 'var(--color-text-2)',
          }}>
            <span>{p.etiqueta}</span>
            <span style={{
              fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums',
              color: p.n > 0 ? 'var(--color-text)' : 'var(--color-text-3)',
            }}>
              {p.n}
            </span>
          </div>
        ))}
      </div>
    </Tarjeta>
  )
}

// ── tiers: barra apilada horizontal por grupo (opción A de Manuel) ──────────
function BarraTiers({ grupo }) {
  const total = grupo.total || 0
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontSize: 'var(--text-sm)', color: 'var(--color-text-2)',
        marginBottom: 'var(--space-1)',
      }}>
        <span>{grupo.etiqueta}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--color-text)' }}>
          {total}
        </span>
      </div>
      {total === 0 ? (
        <div style={{
          height: '10px', borderRadius: '3px',
          background: 'var(--color-surface-2)',
          border: '1px dashed var(--color-border-2)',
        }} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-05)', height: '10px' }}>
            {grupo.segmentos.map(s => (
              <div
                key={s.tier}
                title={`${s.tier} · ${s.n}`}
                style={{
                  flex: s.n, minWidth: '6px', borderRadius: '3px',
                  background: TIER_BAR[s.tier] || 'var(--color-neutral)',
                }}
              />
            ))}
          </div>
          {/* La identidad nunca viaja solo en el color: leyenda con nombre y n. */}
          <div style={{
            marginTop: 'var(--space-1)', display: 'flex', flexWrap: 'wrap',
            gap: '2px 10px', fontSize: 'var(--text-xs)',
            fontFamily: 'var(--font-mono)', color: 'var(--color-text-2)',
          }}>
            {grupo.segmentos.map(s => (
              <span key={s.tier} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{
                  width: '7px', height: '7px', borderRadius: '2px', display: 'inline-block',
                  background: TIER_BAR[s.tier] || 'var(--color-neutral)',
                }} />
                {s.tier} {s.n}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function TarjetaBarras({ c }) {
  return (
    <Tarjeta etiqueta={c.etiqueta} ruta={c.ruta}>
      {/* El vacío es «no hay tenants activos», no «no hay de pago»: desde el
          05-09 esta tarjeta reparte TODOS los activos, incluidos los de alta
          manual, para que su total cuadre con el de la tarjeta de tenants. */}
      {c.valor === 0 ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          Todavía no hay ningún tenant activo
        </div>
      ) : (
        (c.grupos || []).map(g => <BarraTiers key={g.clave} grupo={g} />)
      )}
    </Tarjeta>
  )
}

// ── próxima vuelta programada ───────────────────────────────────────────────
function _horaCorta(iso) {
  // Hora de Chile SIEMPRE (Manuel, 29-08); el proximo_run del scheduler viaja
  // con zona y las demás marcas en el UTC de la casa — el helper resuelve ambas.
  const hora = horaCL(iso)
  if (!hora) return iso
  if (diaISOChileDe(iso) === diaISOChile()) return hora
  return `${fechaCL(iso, { weekday: 'short', day: 'numeric' })} · ${hora}`
}

function TarjetaProxima({ c }) {
  const sinLectura = c.lectura === null || c.lectura === undefined
  return (
    <Tarjeta etiqueta={c.etiqueta} ruta={c.ruta}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xl)',
        fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', lineHeight: 1,
        color: c.valor ? 'var(--color-text)' : 'var(--color-text-3)',
      }}>
        {c.valor ? _horaCorta(c.valor) : '—'}
      </div>
      <div style={{
        ...NOTA,
        color: sinLectura ? 'var(--color-warn)' : 'var(--color-text-3)',
      }}>
        {sinLectura ? 'planificador sin lectura' : (c.contexto || '')}
      </div>
    </Tarjeta>
  )
}

// ── resumen semanal: el pulso del correo de los viernes ─────────────────────
const ESTADO_RESUMEN_COLOR = {
  ok: 'var(--color-ok)',
  error: 'var(--color-error)',
  parcial: 'var(--color-warn)',
  enviando: 'var(--color-warn)',
}

function TarjetaResumen({ c }) {
  const sinEnvios = c.valor === null || c.valor === undefined
  return (
    <Tarjeta etiqueta={c.etiqueta} ruta={c.ruta}>
      {sinEnvios ? (
        <div>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xl)',
            lineHeight: 1, color: 'var(--color-text-3)',
          }}>—</div>
          <div style={NOTA}>{c.contexto || 'sin envíos registrados aún'}</div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xl)',
              fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', lineHeight: 1,
              color: 'var(--color-text)',
            }}>
              {c.valor}
            </span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-ok)' }}>ok</span>
          </div>
          <div style={NOTA}>{c.contexto}</div>
          {(c.estados || []).length > 0 && (
            <div style={{
              marginTop: 'var(--space-1)', display: 'flex', flexWrap: 'wrap',
              gap: '2px 10px', fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)',
            }}>
              {c.estados.map(e => (
                <span key={e.estado} style={{ color: ESTADO_RESUMEN_COLOR[e.estado] || 'var(--color-text-3)' }}>
                  {e.estado} {e.n}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Tarjeta>
  )
}

// ── ensamblado ──────────────────────────────────────────────────────────────
const POR_TIPO = {
  desglose: TarjetaDesglose,
  barras: TarjetaBarras,
  proxima: TarjetaProxima,
  resumen: TarjetaResumen,
}

function TarjetaCifra({ c }) {
  return (
    <Tarjeta etiqueta={c.etiqueta} ruta={c.ruta}>
      <ValorVivo valor={c.valor} lectura={c.lectura} sufijo={c.sufijo} contexto={c.contexto} />
    </Tarjeta>
  )
}

export function ColumnaCifras({ cifras = [], cargando = false }) {
  if (cargando && cifras.length === 0) {
    return (
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
        color: 'var(--color-text-3)',
      }}>
        Leyendo las cifras…
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {cifras.map(c => {
        const Componente = POR_TIPO[c.tipo] || TarjetaCifra
        return <Componente key={c.clave} c={c} />
      })}
    </div>
  )
}
